import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/services/common-services/common.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import * as EmailValidator from 'email-validator';
import { noNumericAfterDotComValidator } from 'src/app/shared/numeric-after-dot-validator';
import { CartService } from '../../../services/cart.service';
import { UserType } from 'src/app/shared/constants/constants';
import { ConfirmationBoxComponent } from 'src/app/shared/elements/confirmation-box/confirmation-box.component';
import { MatDialog } from '@angular/material/dialog';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { getCaptcha, isLocalEnv } from 'src/app/shared/utils';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements AfterViewInit {
  @ViewChild('my_captcha_container', { static: false }) captchaContainer!: ElementRef;
  captchaElementContainer: any;

  baseUrl = environment.BASE_URL;
  portalUrl: string = environment.PORTAL_URL;
  captchaKey = environment.CAPTCHA_API;

  loginForm!: FormGroup;
  rememberMe: FormControl = new FormControl('');
  hide = true;
  urlParams: any;
  companyid!: number;
  isCustomer: boolean = true;
  userType!: UserType;
  preEmailid: string | null = null;
  isReadOnly = true;
  isDashboardRedirect: boolean = false;
  showLoginButton: boolean = true;
  loginError: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private cartService: CartService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    public gtmService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      const refreshToken = params['refreshToken'];
      const accessToken = params['accessToken'];
      let dashboard = params['dashboard'];
      const customercompanyId = params['customercompanyId'];
      const customeruserId = params['customeruserId'];
      const customerEmail = params['email'];
      const redirectToKYC = params['redirectToKYC'] == "true" ? true : false;
      if (customerEmail) {
        sessionStorage.removeItem('preEmail');
        sessionStorage.setItem('preEmail', customerEmail);
      }
      if (dashboard) {
        this.isDashboardRedirect = true;
      }
      const checkToken: any = sessionStorage.getItem('customerToken');
      if (token && refreshToken && accessToken && !checkToken) {
        this.setTokenData(
          token,
          refreshToken,
          accessToken,
          customeruserId,
          customercompanyId,
          redirectToKYC
        );
      } else if (checkToken) {
        this.openConfirmation();
      }
    });

    this.preEmailid = sessionStorage.getItem('preEmail');
    this.rememberEmailAndPassword();
    this.tokenInfo();
    this.getUrlData();
    this.createLoginForm();
  }

  ngAfterViewInit(): void {
    this.captchaElementContainer = this.captchaContainer?.nativeElement;
  }

  createCaptcha(res: any) {
    this.showLoginButton = false;
    const captchaElement = getCaptcha(this.captchaElementContainer, this.captchaKey);
    captchaElement.then((response: boolean) => {
      if (response) {
        this.setUserInfoAndGetPermissions(res);
      } else {
        this.loginError = 'Invalid captcha. Please try again.';
        this.createCaptcha(res); // retry captcha
      }
    });
  }

  onSubmit() {
    this.loginError = '';
    const APIUrl = `${this.baseUrl}auth-service/login`;
    const payload = {
      userName: this.loginForm.value.email,
      email: this.loginForm.value.email,
      passWord: this.commonService.encryptData(this.loginForm.value.passWord),
    };

    this.commonService.postDatawithoutHeaders(APIUrl, payload).subscribe({
      next: (res: any) => {
        this.loginForm.get('passWord')?.disable();
        if (isLocalEnv()) {
          // this.getPermissionsData(userId);
          this.setUserInfoAndGetPermissions(res);
        } else {
          this.createCaptcha(res);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.loginError = error.error.status.error.message;
      },
    });
  }

  setUserInfoAndGetPermissions(res: any) {
    const token = res?.response?.idToken;
    sessionStorage.setItem('customerToken', token);
    const jwt = token;
    const tokens = jwt.split('.');
    const idToken = JSON.parse(atob(tokens[1]));
    const customRole = idToken['custom:role'];
    const userId = idToken['custom:userid'];
    const companyId = idToken['custom:companyId'];

    sessionStorage.setItem('customeruserId', userId);
    sessionStorage.setItem('customercompanyId', companyId);
    sessionStorage.setItem('customerAccessToken', res.response.accessToken);
    sessionStorage.setItem('customerRefreshToken', res.response.refreshToken);
    sessionStorage.setItem('customerRole', customRole);

    this.companyid = companyId;

    this.tokenInfo();
    this.pushLoginEvent(customRole, userId, companyId);
    this.getPermissionsData(userId);
  }

  createLoginForm() {
    const email = sessionStorage.getItem('customeruserEmail');
    const password = sessionStorage.getItem('customerpassword');
    this.loginForm = this.fb.group({
      email: new FormControl(this.preEmailid || '', [
        Validators.required,
        Validators.email,
        noNumericAfterDotComValidator(),
      ]),
      passWord: new FormControl(password || '', [Validators.required]),
    });
  }

  rememberEmailAndPassword() {
    this.rememberMe.valueChanges.subscribe((res: any) => {
      if (res) {
        sessionStorage.setItem('customeruserEmail', this.loginForm.value.email);
        sessionStorage.setItem('customerpassword', this.loginForm.value.passWord);
      } else {
        sessionStorage.removeItem('customeruserEmail');
        sessionStorage.removeItem('customerpassword');
      }
    });
  }

  openConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationBoxComponent, {
      disableClose: true,
      data: {
        cssClass: 'redirectCnt',
        content: `You are already login with other user. If you want proceed then logout first.`,
        btnConfirm: 'OK',
        btnCancel: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.router.navigateByUrl('/');
      }
    });
  }

  getUrlData(): void {
    this.urlParams = this.commonService.getUrlParams();
  }

  tokenInfo() {
    const token = sessionStorage.getItem('customerToken');
    const companyId = sessionStorage.getItem('customercompanyId');
    const userId = sessionStorage.getItem('customeruserId');
    const permissions = sessionStorage.getItem('customerpermissionsData');

    if (token && companyId && permissions && userId) {
      this.router.navigateByUrl('/');
    }
  }

  getPermissionsData(userId: any, redirectToKyc: boolean = false) {
    const apiURL = `${this.baseUrl}auth-service/role-permission-management/${userId}?userRole=Customer`;
    this.commonService.getData(apiURL).subscribe({
      next: (res: any) => {
        this.chekCartData();
        sessionStorage.removeItem('cartData');
        if (res) {
          sessionStorage.setItem('customerpermissionsData', JSON.stringify(res));
          this.cartService.getCartItems(this.companyid);
          this.commonService.checkToken();
          if (redirectToKyc) {
            this.router.navigateByUrl('/profile-update')
          } else {
            this.router.navigateByUrl(this.isDashboardRedirect ? '/dashboard' : '/');
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
      },
    });
  }

  chekCartData() {
    const cartLocalData: any = sessionStorage.getItem('cartData');
    if (cartLocalData) {
      const dataItem = JSON.parse(cartLocalData);
      Object.values(dataItem).forEach((product: any) => this.addToCart(product));
    }
  }

  addToCart(product: any) {
    const payload = {
      productId: product.productId,
      productName: product.productName,
      quantity: product.quantity,
      date: product.date,
      cmsCatName: product.cmsCatName,
      cmsSubCatName: product.cmsSubCatName,
      companyId: this.companyid,
      imageUrl: product.imageUrl,
      rfqTemplate: product.rfqTemplate,
      tags: product.tags,
    };

    const apiUrl = `${this.baseUrl}rfq/cart/addCart`;
    this.commonService.postDatawithoutHeaders(apiUrl, payload).subscribe(() => {
      this.cartService.getCartItems(this.companyid);
    });
  }

  togglePasswordVisibility() {
    this.hide = !this.hide;
  }

  isValid() {
    return this.loginForm.invalid;
  }

  get validateEmail() {
    return EmailValidator.validate(this.loginForm.value.email);
  }
  setTokenData(
    token: any,
    refreshToken: any,
    accessToken: any,
    userId: any,
    companyId: any,
    redirectToKYC: boolean
  ) {
    sessionStorage.setItem('customerToken', token);
    sessionStorage.setItem('customerAccessToken', accessToken);
    sessionStorage.setItem('customerRefreshToken', refreshToken);
    sessionStorage.setItem('customerRole', 'Customer');
    sessionStorage.setItem('customeruserId', userId);
    sessionStorage.setItem('customercompanyId', companyId);

    this.companyid = companyId;
    this.getPermissionsData(userId, redirectToKYC);
  }
  pushLoginEvent(customRole: any, userId: any, companyId: any) {
    const gtmTag = {
      event: 'Customer_Login',
      event_category: 'User Input',
      login_role_customer: customRole,
      login_userId_customer: userId,
      login_companyId_customer: companyId,
      login_email_customer: sessionStorage.getItem('preEmail'),
    };
    this.gtmService.pushTag(gtmTag);
  }

  redirectToForgotPassword(): void {
    this.router.navigateByUrl('/login/forget-password');
  }
}
